import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const Card = ({ title, desc, children, testid, onSubmit }) => (
  <form className="bento-card rounded-2xl p-5 md:p-6 flex flex-col gap-3" data-testid={testid} onSubmit={onSubmit}>
    <h3 className="font-display text-xl font-bold text-white">{title}</h3>
    <p className="text-sm text-slate-400 -mt-1">{desc}</p>
    {children}
  </form>
);

const Submit = ({ children, testid, busy }) => (
  <button type="submit" disabled={busy} data-testid={testid} className="btn-chrome self-start rounded-full px-5 py-2 text-sm font-semibold text-[#050713] mt-1">{children}</button>
);

export const SettingsTab = () => {
  const { t } = useLang();
  const { refresh } = useAuth();
  const s = t.admin.settings;
  const [notify, setNotify] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [email, setEmail] = useState({ new_email: "", password: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", repeat: "" });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => { setNotify(data.notify_email || ""); setLoginEmail(data.login_email); }).catch(() => {});
  }, []);

  const run = (key, fn) => async (e) => {
    e.preventDefault();
    setBusy(key);
    try { await fn(); } catch (err) { toast.error(errMsg(err, t.common.error)); } finally { setBusy(""); }
  };

  const saveNotify = run("notify", async () => {
    const { data } = await api.put("/admin/settings", { notify_email: notify });
    setNotify(data.notify_email);
    toast.success(s.saved);
  });

  const saveEmail = run("email", async () => {
    const { data } = await api.post("/admin/change-email", email);
    setLoginEmail(data.login_email);
    setEmail({ new_email: "", password: "" });
    toast.success(s.emailSaved);
    refresh?.();
  });

  const savePwd = run("pwd", async () => {
    if (pwd.new_password !== pwd.repeat) return toast.error(s.mismatch);
    await api.post("/admin/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
    setPwd({ current_password: "", new_password: "", repeat: "" });
    toast.success(s.pwdSaved);
  });

  return (
    <div className="mt-6 grid lg:grid-cols-2 gap-4">
      <Card title={s.notifyTitle} desc={s.notifyDesc} testid="settings-notify-card" onSubmit={saveNotify}>
        <input type="email" required className="field" value={notify} onChange={(e) => setNotify(e.target.value)} placeholder={s.notifyEmail} data-testid="settings-notify-email-input" />
        <Submit testid="settings-notify-save" busy={busy === "notify"}>{s.save}</Submit>
      </Card>
      <Card title={s.loginTitle} desc={`${s.loginDesc} (${loginEmail})`} testid="settings-login-card" onSubmit={saveEmail}>
        <input type="email" required className="field" value={email.new_email} onChange={(e) => setEmail({ ...email, new_email: e.target.value })} placeholder={s.newEmail} data-testid="settings-new-email-input" />
        <input type="password" required className="field" value={email.password} onChange={(e) => setEmail({ ...email, password: e.target.value })} placeholder={s.confirmPassword} data-testid="settings-email-password-input" />
        <Submit testid="settings-email-save" busy={busy === "email"}>{s.update}</Submit>
      </Card>
      <Card title={s.pwdTitle} desc={s.pwdDesc} testid="settings-password-card" onSubmit={savePwd}>
        <input type="password" required className="field" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} placeholder={s.current} data-testid="settings-current-password-input" />
        <input type="password" required minLength={8} className="field" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} placeholder={s.newPwd} data-testid="settings-new-password-input" />
        <input type="password" required minLength={8} className="field" value={pwd.repeat} onChange={(e) => setPwd({ ...pwd, repeat: e.target.value })} placeholder={s.repeat} data-testid="settings-repeat-password-input" />
        <Submit testid="settings-password-save" busy={busy === "pwd"}>{s.change}</Submit>
      </Card>
    </div>
  );
};
